import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { PagedResult } from "@iventis/domain-model/model/pagedResult";
import qs from "qs";
import { useQueryClient } from "@tanstack/react-query";
import { aggregatePagedResponseLists } from "@iventis/api-helpers";
import { LIST_ITEMS_QUERY_KEY } from "@iventis/datafield-editor";
import { FilterFormat } from "@iventis/types";
import { listItemToCreateDto } from "@iventis/utilities";
import { mappingApi } from "@iventis/api/src/api";
import { Content } from "@iventis/translations";
import { translate } from "@iventis/translations/translation";

export const createLayerDataFieldListItem = async (request: { layerId: string; dataFieldId: string; dataFieldListItem: DataFieldListItem }) => {
    const response = await mappingApi.post<DataFieldListItem>(`/data-fields/${request.dataFieldId}/list-items`, [listItemToCreateDto(request.dataFieldListItem)], {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.attributes.attribute_list_item) }),
            message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.attributes.attribute_list_item) }),
        },
    });
    return response.data;
};

export const updateLayerDataFieldListItem = async (request: { layerId: string; dataFieldId: string; dataFieldListItem: DataFieldListItem }) => {
    const response = await mappingApi.patch<DataFieldListItem>(`/data-fields/${request.dataFieldId}/list-items/${request.dataFieldListItem.id}`, request.dataFieldListItem, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.attributes.attribute_list_item) }),
            message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.attributes.attribute_list_item) }),
        },
    });
    return response.data;
};

// needed so we can update the order
export const getDataFieldListItem = async (dataFieldId: string, listItemId: string) => {
    const response = await mappingApi.get<DataFieldListItem>(`/data-fields/${dataFieldId}/list-items/${listItemId}`);
    return response.data;
};

export const updateLayerDataFieldListItemOrder = async (dataFieldId: string, listItem: { id: string; listItemIdAfter: string; order: number }) => {
    const response = await mappingApi.patch<DataFieldListItem>(
        `/data-fields/${dataFieldId}/list-items/${listItem.id}`,
        {
            listItemIdAfter: listItem.listItemIdAfter,
            order: listItem.order,
        },
        {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.attributes.attribute_list_item_order) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.attributes.attribute_list_item_order) }),
            },
        }
    );
    return response.data;
};

export const deleteLayerDataFieldListItem = async (request: { layerId: string; dataFieldId: string; dataFieldListItemId: string }) => {
    const response = await mappingApi.delete<string>(`/data-fields/${request.dataFieldId}/list-items/`, {
        params: { ids: [request.dataFieldListItemId] },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.delete_error.title, { entity: translate(Content.attributes.attribute_list_item) }),
            message: translate(Content.errors.custom_network_error.delete_error.message, { entity: translate(Content.attributes.attribute_list_item) }),
        },
    });
    return response.data;
};

export const importDataFieldListItems = async (request: { dataFieldId: string; csv: string; deleteExisting?: boolean }) => {
    await mappingApi.post<void>(`/data-fields/${request.dataFieldId}/list-items/import`, request.csv, {
        headers: {
            "Content-Type": "application/json",
        },
        params: { deleteExisting: request.deleteExisting },
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.import_error.title, { entity: translate(Content.attributes.attribute_list_items) }),
            message: translate(Content.errors.custom_network_error.import_error.message, { entity: translate(Content.attributes.attribute_list_items) }),
        },
    });
};

export const exportDataFieldListItems = async (request: { dataFieldId: string }, notificationId: string) => {
    await mappingApi.post<DataFieldListItem[]>(
        `/data-fields/${request.dataFieldId}/list-items/export`,
        { notificationId },
        {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.export_error.title, { entity: translate(Content.attributes.attribute_list_items) }),
                message: translate(Content.errors.custom_network_error.export_error.message, { entity: translate(Content.attributes.attribute_list_items) }),
            },
        }
    );
};

export const getDataFieldListItems = (queryClient: ReturnType<typeof useQueryClient>, checkCache = false) => async (dataFieldId: string, filters: FilterFormat[]) => {
    // Most of the time, these calls will be made inside components using the useQuery hook which manages the cache internally.
    // But in some cases, we call it from outside these components (for example the map engine) and so we must check in the cache first in those cases
    if (checkCache) {
        // Check first if the data exists in cache
        const listItemsInCache =
            filters == null
                ? queryClient.getQueryData<DataFieldListItem[]>([LIST_ITEMS_QUERY_KEY, dataFieldId])
                : queryClient.getQueryData<DataFieldListItem[]>([LIST_ITEMS_QUERY_KEY, dataFieldId, filters]);
        if (listItemsInCache) {
            return listItemsInCache;
        }
    }
    // Get all the list items for now since the list item data table is client side only.
    // NOTE this function call is not just used in the data table, but also in map objects etc.
    const result = await aggregatePagedResponseLists(async (pageNumber) => {
        const res = await mappingApi.get<PagedResult<DataFieldListItem[]>>(`data-fields/${dataFieldId}/list-items/filter`, {
            params: { filter: JSON.stringify(filters), pageNumber },
            paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
        });
        return res.data;
    });
    return result;
};
